/* eslint-disable prettier/prettier */
import { useCallback, useMemo } from 'react';
import { useSearchParams, createSearchParams } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';

moment.locale('pt-br');

interface UseMonthFilterOptions {
  defaultToPreviousMonth?: boolean;
  defaultSize?: string;
  additionalParams?: Record<string, string>;
}

export function useMonthFilter(options: UseMonthFilterOptions = {}) {
  const { defaultToPreviousMonth = true, defaultSize = '50', additionalParams = {} } = options;

  // Calcular mês inicial (anterior ou atual)
  const initialMonth = useMemo(() => {
    return defaultToPreviousMonth ? moment().subtract(1, 'month') : moment();
  }, [defaultToPreviousMonth]);

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      page: '1',
      size: defaultSize,
      date_start: initialMonth.startOf('month').format('YYYY-MM-DD'),
      date_end: initialMonth.clone().endOf('month').format('YYYY-MM-DD'),
      ...additionalParams,
    }),
  );

  // Data atual baseada nos filtros
  const currentDate = useMemo(() => {
    const dateStart = filters.get('date_start');
    return dateStart ? moment(dateStart) : initialMonth.clone();
  }, [filters, initialMonth]);

  const isCurrentMonth = currentDate.isSame(moment(), 'month');

  // Handlers
  const handlePrevMonth = useCallback(() => {
    const prevMonth = currentDate.clone().subtract(1, 'month');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: prevMonth.startOf('month').format('YYYY-MM-DD'),
        date_end: prevMonth.endOf('month').format('YYYY-MM-DD'),
        page: '1',
      }),
    );
  }, [currentDate, filters, setFilters]);

  const handleNextMonth = useCallback(() => {
    const nextMonth = currentDate.clone().add(1, 'month');
    if (!nextMonth.isAfter(moment(), 'month')) {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: nextMonth.startOf('month').format('YYYY-MM-DD'),
          date_end: nextMonth.endOf('month').format('YYYY-MM-DD'),
          page: '1',
        }),
      );
    }
  }, [currentDate, filters, setFilters]);

  const handleSelectMonth = useCallback(
    (year: number, month: number) => {
      const selectedDate = moment().year(year).month(month);
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: selectedDate.startOf('month').format('YYYY-MM-DD'),
          date_end: selectedDate.clone().endOf('month').format('YYYY-MM-DD'),
          page: '1',
        }),
      );
    },
    [filters, setFilters],
  );

  return {
    currentDate,
    dateStart: filters.get('date_start') || '',
    dateEnd: filters.get('date_end') || '',
    isCurrentMonth,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
    setFilters,
  };
}

// Função utilitária para obter o mês anterior (pode ser usada fora do hook)
export function getPreviousMonthDates() {
  const previousMonth = moment().subtract(1, 'month');
  return {
    date_start: previousMonth.startOf('month').format('YYYY-MM-DD'),
    date_end: previousMonth.clone().endOf('month').format('YYYY-MM-DD'),
  };
}

// Função utilitária para obter o mês atual
export function getCurrentMonthDates() {
  const currentMonth = moment();
  return {
    date_start: currentMonth.startOf('month').format('YYYY-MM-DD'),
    date_end: currentMonth.clone().endOf('month').format('YYYY-MM-DD'),
  };
}
